// IVR switch sync — Phases 0-2 of docs/design/2026-10-14_ivr_app_management.md.
// Backend: IvrSwitchSyncController (/ivr/switch-sync). Import, ownership filing,
// drift and push audit are Green Giraffe only; /my-flows is a business's read-only
// view of the flows filed to it. Nothing here writes to a switch except rollback
// (master only, preview first).
import axiosInstance from '../axios.config';
import { apiError } from '../apiError';
import type { ApiResponse } from 'types';

export interface FlowVersionSummary {
  versionId: number;
  providerId: number;
  provider?: string;
  language: string;
  versionNo: number;
  source: 'RUNTIME_IMPORT' | 'PUSH' | 'ROLLBACK';
  stateCount?: number;
  /** The recording folder. Read-only here. */
  businessNames?: string | null;
  duplicateKeys?: string | null;
  checksum: string;
  createdAt?: string;
  createdBy?: string;
  drift: DriftSummary;
}

export interface DriftSummary {
  status: 'IN_SYNC' | 'DRIFTED' | 'MISSING_ON_SWITCH' | 'NOT_IMPORTED' | 'UNREACHABLE' | 'UNCHECKED';
  checkedAt?: string;
  added?: number;
  removed?: number;
  changed?: number;
  duplicates?: number;
  businessNameChanged?: boolean;
}

export interface FlowNumber { providerId: number; cliNumber: string; holderOrgId?: string | null; holderOrgName?: string | null }

export interface Ownership {
  status: 'BY_NUMBERS' | 'UNASSIGNED' | 'ASSIGNED_BY_MASTER' | 'NOT_FILED';
  ownerOrgId?: string | null;
  ownerOrgName?: string | null;
  suggestedOrgId?: string | null;
  suggestedOrgName?: string | null;
  reason?: string | null;
  numbers?: FlowNumber[];
}

export interface CatalogApp {
  operatorId: string;
  serviceId: string;
  ownership: Ownership;
  /** Only on /my-flows: whether the strict own-numbers rule lets this org manage it. */
  manageable?: boolean;
  manageReason?: string;
  flows: FlowVersionSummary[];
}

export interface DriftRow extends DriftSummary {
  providerId: number;
  provider?: string;
  operatorId: string;
  serviceId: string;
  language: string;
  baselineVersionId?: number;
  report?: {
    added?: string[];
    removed?: string[];
    changed?: Array<{ key: string; diff: Record<string, { before: unknown; after: unknown }> }>;
    businessNamesBefore?: string[];
    businessNamesNow?: string[];
    duplicates?: string[];
    error?: string;
    note?: string;
  } | null;
}

export interface PushAudit {
  id: number;
  kind: 'PUSH' | 'ROLLBACK';
  rollbackOfId?: number | null;
  rolledBackById?: number | null;
  providerId: number;
  provider?: string;
  operatorId: string;
  serviceId: string;
  language: string;
  callRequest?: string | null;
  businessNameBefore?: string | null;
  businessNameAfter?: string | null;
  result: 'APPLIED' | 'PARTIAL' | 'FAILED';
  rowsWritten?: number;
  rowsDeleted?: number;
  pushedBy?: string;
  pushedAt?: string;
}

const BASE = '/ivr/switch-sync';

async function call<T>(fn: () => Promise<{ data: any }>, fallback: string): Promise<ApiResponse<T>> {
  try {
    const res = await fn();
    return { success: true, data: res.data?.data as T };
  } catch (e) {
    return apiError(e, fallback);
  }
}

export const ivrSwitchSyncService = {
  catalog: () => call<CatalogApp[]>(() => axiosInstance.get(`${BASE}/catalog`), 'Could not load the IVR catalogue'),
  myFlows: (organizationId?: string) =>
    call<CatalogApp[]>(() => axiosInstance.get(`${BASE}/my-flows`, { params: { organizationId } }),
      'Could not load your IVR flows'),
  importAll: (apply: boolean, providerId?: number) =>
    call<Record<string, any>>(() => axiosInstance.post(`${BASE}/import`, null, { params: { apply, providerId } }),
      'Import failed'),
  assignOwner: (operatorId: string, serviceId: string, organizationId?: string) =>
    call<Ownership>(() => axiosInstance.put(`${BASE}/ownership`, null, { params: { operatorId, serviceId, organizationId } }),
      'Could not file the flow'),
  checkDrift: (operatorId?: string, serviceId?: string, maxApps = 50) =>
    call<unknown>(() => axiosInstance.post(`${BASE}/drift/check`, null, { params: { operatorId, serviceId, maxApps } }),
      'Drift check failed'),
  findUnimported: () =>
    call<Record<string, unknown>>(() => axiosInstance.post(`${BASE}/drift/find-unimported`), 'Could not list the switches'),
  drift: () => call<DriftRow[]>(() => axiosInstance.get(`${BASE}/drift`), 'Could not load drift'),
  pushes: (limit = 50) => call<PushAudit[]>(() => axiosInstance.get(`${BASE}/pushes`, { params: { limit } }),
    'Could not load the push history'),
  /** apply needs the previewHash the rollback preview returned (409 if the switch changed since). */
  rollback: (id: number, apply: boolean, removeInserted = false, previewHash?: string) =>
    call<Record<string, any>>(() => axiosInstance.post(`${BASE}/pushes/${id}/rollback`, null,
      { params: { apply, removeInserted, previewHash } }),
      apply ? 'Rollback failed' : 'Could not preview the rollback'),
};

/** Badge colour per drift status. */
export function driftTone(status?: string): string {
  switch (status) {
    case 'IN_SYNC': return 'bg-green-100 text-green-800';
    case 'DRIFTED': return 'bg-red-100 text-red-800';
    case 'MISSING_ON_SWITCH': return 'bg-red-100 text-red-800';
    case 'UNREACHABLE': return 'bg-amber-100 text-amber-800';
    case 'NOT_IMPORTED': return 'bg-blue-100 text-blue-800';
    default: return 'bg-gray-100 text-gray-700';
  }
}

/** The worst drift status across an app's flows — what the row badge shows. */
export function worstDrift(flows: FlowVersionSummary[]): DriftSummary['status'] {
  const order: DriftSummary['status'][] = ['DRIFTED', 'MISSING_ON_SWITCH', 'UNREACHABLE', 'NOT_IMPORTED', 'UNCHECKED', 'IN_SYNC'];
  let best = order.length - 1;
  for (const f of flows) {
    const i = order.indexOf(f.drift?.status ?? 'UNCHECKED');
    if (i >= 0 && i < best) best = i;
  }
  return flows.length ? order[best] : 'UNCHECKED';
}
